import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthProvider';
import type { SupabaseClient } from '../../lib/supabase';
import { trackerKeys } from './hooks';
import { endedReasonLabel, roundProgress } from './leadRounds';
import type { RoundProgress, RunningRound } from './leadRounds';

/**
 * Where a lead sits in the follow-up loop, for the list rather than the drawer.
 *
 * ── The rule this makes visible ────────────────────────────────────────────
 *
 * Kyu: "every lead should always be scheduled or inside a round." Three
 * permitted states and no fourth:
 *
 *   in a round            a round is running
 *   needs next round      a round ENDED and nobody scheduled the next one
 *   never in a round      nobody has begun
 *
 * The second is the one that matters and the one that is invisible without
 * this module. From `lead_round_state` alone, a lead dropped after three
 * rounds and a lead nobody has ever touched look identical: neither has a
 * running round. One of them is a failure and the other is a queue.
 *
 * ── Two questions, both answered by counting ──────────────────────────────
 *
 * Neither is an opinion about whether somebody is doing their job:
 *
 *   due today      as many follow-ups left as days left, so today is one of
 *                  the days you have to use
 *   running out    MORE follow-ups left than days left, so this round can no
 *                  longer be finished
 *
 * The second replaced "behind" on 29 Aug. Kyu: "3/7 behind — well are we
 * really behind, or are we just at the 3rd email yet?" A round is capped at
 * one touch a day, so the old pace rule called a lead behind for a gap it had
 * no spare day to close. Running out says the same thing when it is true and
 * says nothing when it is not.
 *
 * Both handle the weekend without a special case. A business-day window does
 * not advance over Saturday and Sunday, so a lead whose Friday touch went out
 * owes nothing until Monday — Kyu's rule that weekends are not misses, falling
 * out of the arithmetic instead of being written down twice.
 */

export type LoopState = 'in-round' | 'needs-next-round' | 'never-in-a-round';

/** One row of `lead_loop_state`: the lead's whole round history, in summary. */
export interface LeadLoop {
  leadId: string;
  roundsRun: number;
  inRound: boolean;
  lastEndedNumber: number | null;
  lastEndedOn: string | null;
  lastEndedReason: string | null;
}

export function loopStateOf(loop: LeadLoop | undefined): LoopState {
  if (loop === undefined || loop.roundsRun === 0) {
    return 'never-in-a-round';
  }
  return loop.inRound ? 'in-round' : 'needs-next-round';
}

/**
 * Today is one of the days this round has to use.
 *
 * True when there are at least as many follow-ups left as days left: with one
 * touch a day as the ceiling, skipping today makes the round impossible.
 *
 * A round that has not started yet owes nothing, and neither does one whose
 * window has closed — that one is waiting on the sweep to end it, and dressing
 * it up as work outstanding would put a lead in the daily queue that nobody
 * can act on.
 */
export function isDueToday(progress: RoundProgress | null): boolean {
  if (progress === null || progress.pace === 'not-started' || progress.touchesLeft === 0) {
    return false;
  }
  // Against daysToSendIn, which includes today. daysLeft excludes it, and
  // comparing against that made a fresh round look owed on its first morning.
  return progress.touchesLeft >= progress.daysToSendIn;
}

/** More follow-ups left than days to send them in. Arithmetic, not opinion. */
export function isRunningOut(progress: RoundProgress | null): boolean {
  return progress !== null && progress.pace === 'running-out';
}

/** The running round's progress, or null when the lead is not in one. */
export function progressOf(running: RunningRound | undefined, today: string): RoundProgress | null {
  if (running === undefined) {
    return null;
  }
  if (today > running.windowEnd) {
    // The window has passed and close_due_rounds has not run yet. Reporting a
    // pace here would say "behind by 7" about a round that is over.
    return null;
  }
  return roundProgress({
    startsOn: running.startsOn,
    touchTarget: running.touchTarget,
    windowLength: running.windowLength,
    windowUnit: running.windowUnit,
    touchesDone: running.touchesDone,
    windowEnd: running.windowEnd,
    today,
  });
}

/** What the Rounds column says, in as few words as fit in a table cell. */
export function loopCellLabel(
  state: LoopState,
  running: RunningRound | undefined,
  loop: LeadLoop | undefined,
): string {
  if (state === 'in-round' && running !== undefined) {
    return `Round ${String(running.number)} · ${String(running.touchesDone)}/${String(running.touchTarget)}`;
  }
  if (state === 'needs-next-round') {
    // Named by what happened, not by what is missing. "Round 2 ended — they
    // replied" and "Round 2 ended — window closed, no answer" call for
    // completely different next moves, and a cell that says "needs next round"
    // for both makes somebody open the drawer to find out which.
    const number = loop?.lastEndedNumber;
    const which = number === null || number === undefined ? 'A round' : `Round ${String(number)}`;
    return `${which} ended — ${endedReasonLabel(loop?.lastEndedReason ?? null)}`;
  }
  if (state === 'in-round') {
    // In a round by the history, but the running row has not arrived. Say so
    // rather than showing a number that is not loaded.
    return 'In a round';
  }
  return '';
}

// ── Reading it ─────────────────────────────────────────────────────────────

export const loopKeys = {
  all: () => [...trackerKeys.all, 'loop'] as const,
};

/**
 * Every lead that has ever been in a round, keyed by lead.
 *
 * Bounded by how many leads have been worked, not by how many exist, and one
 * request rather than one per row — the lesson the follow-up column learned
 * when `.in('lead_id', ids)` over 787 leads produced a 31,000-character URL.
 *
 * A row missing what it needs is DROPPED rather than filled in with a guess:
 * every column of a view is nullable to the type generator, and a `rounds_run`
 * that arrived null would otherwise become a zero, which reads as "never in a
 * round" — the exact claim this module exists to stop being made by accident.
 */
export async function fetchLeadLoops(client: SupabaseClient): Promise<Map<string, LeadLoop>> {
  const { data, error } = await client.from('lead_loop_state').select('*');
  if (error) {
    throw new Error(error.message);
  }
  const byLead = new Map<string, LeadLoop>();
  for (const row of data ?? []) {
    if (row.lead_id === null || row.rounds_run === null || row.in_round === null) {
      continue;
    }
    byLead.set(row.lead_id, {
      leadId: row.lead_id,
      roundsRun: row.rounds_run,
      inRound: row.in_round,
      lastEndedNumber: row.last_ended_number,
      lastEndedOn: row.last_ended_on,
      lastEndedReason: row.last_ended_reason,
    });
  }
  return byLead;
}

export function useLeadLoops() {
  const { client } = useAuth();
  return useQuery({
    queryKey: loopKeys.all(),
    queryFn: () => fetchLeadLoops(client),
    retry: false,
  });
}
